/**
 * The Live Share `SessionController` (BUILD-SPEC.md §5) plus the
 * `ControllerFactory` (BUILD-SPEC §5 "Fallback") that the UI actually
 * imports.
 *
 * Two things this file is responsible for getting right, beyond the plain
 * "call the engine, then `LiveState.set()`" plumbing:
 *
 *  (b) On the timer's `finished` event, write the turn's zero-crossing into
 *      `Session.zeroPointAt` (see `timer.ts`'s `zeroPointFromConfig`) so
 *      every screen renders the same `+m:ss` overtime figure. The engine
 *      leaves this field `null` — this layer owns it.
 *
 *  (c) `finished` has no `local` flag and fires on every screen; only the
 *      client whose `TurnTimer` reports ownership (see timer.ts) performs
 *      the resulting `LiveState.set()`. This file adds one more check on
 *      top of that: the write only happens if the session is still
 *      `"running"` for the exact `idx` this client started the segment for
 *      — closing the race where Next (or End) fires in the same instant the
 *      old segment naturally reaches zero.
 *
 * `LiveState.set()` replaces the whole object, so every mutating method
 * below writes exactly once, only on the discrete events BUILD-SPEC §5
 * lists (start, next, pause, resume, extend, add, end) plus the guarded
 * zero-point write above — never per tick.
 */
import type { LiveState, LivePresence } from "@microsoft/live-share";
import { LiveEvent } from "@microsoft/live-share";
import type { Session, QueueEntry, Settings } from "../engine";
import {
  addInvited,
  addManual,
  elapsedFor,
  end as engineEnd,
  extend as engineExtend,
  limitFor,
  next as engineNext,
  pause as enginePause,
  reset as engineReset,
  resume as engineResume,
  start as engineStart,
} from "../engine";
import type { AppOpenUser, ControllerFactory, SessionController } from "../contract";
import { joinLiveShare } from "./client";
import { TurnTimer } from "./timer";
import { createLocalController } from "../local/localTimer";

/** Shape of the event Live Share's `LiveEvent.isNewer` compares. Declared
 * locally for the same reason as `TimerConfig` in timer.ts: the spec
 * documents the mechanism (§4 "Use LiveEvent.isNewer(current, received,
 * debouncePeriod)"), not a confirmed exported type name, and the package
 * was not available to check directly in this workspace. */
interface ILiveEvent {
  clientId: string;
  name: string;
  timestamp: number;
}

/**
 * Debounce window for `LiveEvent.isNewer`. This is the *second* line of
 * defence against two people acting at once — the engine's `expectedIdx`
 * check in `next()` is the first, and is sufficient on its own to stop a
 * double-advance. This one instead protects the subscriber-facing cache in
 * this class from applying an out-of-order `stateChanged` delivery (e.g. a
 * slightly-delayed network packet arriving after a newer one already has).
 * A small, non-zero window is deliberate: two `set()` calls issued within
 * the same instant should not both be treated as "definitely newer" than
 * each other purely on tie-broken timestamp order.
 */
const IS_NEWER_DEBOUNCE_MS = 100;

function buildInitialSession(settings: Settings): Session {
  return {
    phase: "ticklist",
    queue: [],
    idx: 0,
    limitSeconds: settings.secondsPerPerson,
    graceSeconds: settings.graceSeconds,
    turnStartedAt: null,
    zeroPointAt: null,
    lastActionBy: "",
    lastActionAt: 0,
  };
}

export interface ControllerIdentity {
  /** Display name used for `Session.lastActionBy` attribution. */
  displayName: string;
  /** Teams user id, used only for the cosmetic `appOpenUsers()` marker. */
  userId: string;
}

class LiveSessionController implements SessionController {
  readonly shared = true;

  private cached: Session;
  private readonly turnTimer: TurnTimer;
  private readonly listeners = new Set<() => void>();
  private lastAppliedEvent: ILiveEvent | undefined;
  /** The `idx` this client started the current shared-timer segment for —
   * part of guard (c)'s second line of defence (see file doc). */
  private pendingTurnIdx: number | null = null;
  private readonly presenceUsers = new Map<string, AppOpenUser>();
  private disposed = false;

  constructor(
    private readonly stateObj: LiveState<Session>,
    turnTimer: TurnTimer,
    private readonly presence: LivePresence,
    private readonly clock: () => number,
    private readonly identity: ControllerIdentity
  ) {
    this.cached = this.stateObj.state;
    this.turnTimer = turnTimer;

    this.stateObj.on(
      "stateChanged",
      (state: Session, local: boolean, clientId: string) => {
        const candidate: ILiveEvent = {
          clientId,
          name: "session",
          timestamp: state.lastActionAt,
        };
        if (!LiveEvent.isNewer(this.lastAppliedEvent, candidate, IS_NEWER_DEBOUNCE_MS) && !local) {
          return; // stale/out-of-order relative to what we already have.
        }
        this.lastAppliedEvent = candidate;
        this.cached = state;
        this.notify();
      }
    );

    this.turnTimer.onOwnedFinish((zeroPointAt) => {
      const state = this.stateObj.state;
      if (
        state.phase === "running" &&
        state.zeroPointAt === null &&
        state.idx === this.pendingTurnIdx
      ) {
        this.stateObj.set({ ...state, zeroPointAt });
      }
    });

    this.setupPresence();
  }

  private setupPresence(): void {
    // LivePresence API, verified against @microsoft/live-share@1.4.2 by
    // reading LivePresence.d.ts / LivePresenceUser.d.ts:
    //   update(data?, state?)             — publish our own presence
    //   getUsers(filter?)                 — LivePresenceUser[]
    //   on("presenceChanged", (user, local, clientId) => void)
    // LivePresenceUser exposes `userId` and `displayName` as getters;
    // displayName comes from Teams, so we do not need to publish it ourselves.
    //
    // Presence is cosmetic only (BUILD-SPEC §5/§6): it drives a small "has the
    // app open" marker and nothing else. Failures are swallowed so that
    // appOpenUsers() stays empty rather than breaking the app (hard rule 6).
    //
    // Note: do NOT make these calls optional (`update?.()`). If a name is
    // wrong, an optional call silently does nothing and the marker is dead
    // with no error to find. Let it throw into the catch instead.
    const refresh = (): void => {
      try {
        const users = this.presence.getUsers();
        this.presenceUsers.clear();
        for (const u of users) {
          if (!u.userId) continue;
          this.presenceUsers.set(u.userId, {
            userId: u.userId,
            displayName: u.displayName ?? "",
          });
        }
        this.notify();
      } catch {
        // Cosmetic only — keep whatever we had.
      }
    };

    try {
      this.presence.on("presenceChanged", refresh);
      void this.presence.update();
      refresh();
    } catch {
      // Presence unavailable entirely — appOpenUsers() just stays empty.
    }
  }

  get session(): Session {
    return this.cached;
  }

  now(): number {
    return this.clock();
  }

  canControl(): boolean {
    return true; // BUILD-SPEC hard rule 5: no role gating.
  }

  appOpenUsers(): AppOpenUser[] {
    return Array.from(this.presenceUsers.values());
  }

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  /** Writes a new Session to LiveState, and — if the new phase is
   * "running" — (re)starts the shared timer for it. This is the single
   * choke point every mutating method funnels through, so "only write on
   * discrete events" (BUILD-SPEC §5) is structural rather than a
   * per-method habit. */
  private commit(next: Session): void {
    if (next === this.cached) return; // engine dropped a stale/no-op call.
    this.stateObj.set(next);
    this.cached = next;
    if (next.phase === "running") {
      this.restartTimerFor(next);
    }
  }

  /**
   * (Re)starts the shared LiveTimer so it will naturally reach zero at the
   * turn's true zero-crossing. BUILD-SPEC §5 mandates this on hand-off
   * ("call start(limitMs) again"); this also covers resume and extend,
   * since both can move the zero-crossing and a `finished` derived from a
   * stale duration would produce the wrong `zeroPointAt`. `elapsedFor`
   * already accounts for banked `spentSeconds` plus any live portion, so
   * `limitFor - elapsedFor` is correct as the remaining duration in every
   * one of those cases.
   */
  private restartTimerFor(state: Session): void {
    const idx = state.idx;
    const now = this.clock();
    const remainingMs = (limitFor(state, idx) - elapsedFor(state, idx, now)) * 1000;
    this.pendingTurnIdx = idx;
    this.turnTimer.start(remainingMs);
  }

  start(order: QueueEntry[], settings: Settings): void {
    const now = this.clock();
    this.commit(engineStart(this.cached, { order, settings, now, by: this.identity.displayName }));
  }

  next(expectedIdx: number): void {
    const now = this.clock();
    this.commit(
      engineNext(this.cached, { expectedIdx, now, by: this.identity.displayName })
    );
  }

  pause(): void {
    const now = this.clock();
    const paused = enginePause(this.cached, { now, by: this.identity.displayName });
    if (paused === this.cached) return;
    // Engine's pause() only spreads the previous Session, so a zeroPointAt
    // set by an earlier overtime finish() would otherwise survive into the
    // paused state even though it no longer means anything (turnStartedAt
    // is null while paused, so nothing derives "+m:ss" from it) — clear it
    // explicitly, mirroring turnStartedAt's own null-while-paused contract.
    const next: Session = paused.zeroPointAt === null ? paused : { ...paused, zeroPointAt: null };
    this.stateObj.set(next);
    this.cached = next;
    this.turnTimer.pause(); // freeze in place; does not move the zero-crossing.
  }

  resume(): void {
    const now = this.clock();
    this.commit(engineResume(this.cached, { now, by: this.identity.displayName }));
  }

  extend(seconds: number): void {
    const now = this.clock();
    const next = engineExtend(this.cached, { seconds, now, by: this.identity.displayName });
    if (next === this.cached) return;
    this.stateObj.set(next);
    this.cached = next;
    // extend() doesn't change `phase`, so it doesn't go through `commit()`'s
    // running-phase check; restart explicitly whenever there's a live turn.
    if (next.phase === "running") {
      this.restartTimerFor(next);
    }
  }

  addManual(displayName: string): void {
    const now = this.clock();
    const next = addManual(this.cached, { displayName, now, by: this.identity.displayName });
    this.stateObj.set(next);
    this.cached = next;
  }

  addInvited(userId: string, displayName: string): void {
    const now = this.clock();
    const next = addInvited(this.cached, {
      userId,
      displayName,
      now,
      by: this.identity.displayName,
    });
    this.stateObj.set(next);
    this.cached = next;
  }

  end(): void {
    const now = this.clock();
    this.commit(engineEnd(this.cached, { now, by: this.identity.displayName }));
  }

  newStandup(): void {
    const now = this.clock();
    this.commit(engineReset(this.cached, { now, by: this.identity.displayName }));
    // Stop the shared timer as well. `commit` only restarts it for a running
    // phase, so nothing would restart it here — but a timer left running
    // could still deliver a `finished` for the standup we just cleared. The
    // staleness guard in TurnTimer would drop that write anyway; pausing
    // makes the intent explicit rather than relying on the guard.
    this.turnTimer.pause();
  }

  resumeFromFinished(): void {
    // "Resume standup" after an accidental End: go back to the last running
    // turn. The engine has no dedicated transition for this (End is meant
    // to be terminal), so this is the one place session.ts reaches past
    // queue.ts's transitions — restoring `phase: "running"` and giving the
    // current speaker a fresh `turnStartedAt` (their banked `spentSeconds`
    // is untouched, so no time is lost or invented).
    const state = this.cached;
    if (state.phase !== "finished" || state.queue.length === 0) return;
    const now = this.clock();
    const idx = Math.min(state.idx, state.queue.length - 1);
    const queue = state.queue.map((entry, i) =>
      i === idx ? { ...entry, status: "speaking" as const } : entry
    );
    const next: Session = {
      ...state,
      phase: "running",
      queue,
      idx,
      turnStartedAt: now,
      zeroPointAt: null,
      lastActionBy: this.identity.displayName,
      lastActionAt: now,
    };
    this.commit(next);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.listeners.clear();
  }
}

export interface CreateControllerOptions {
  settings: Settings;
  identity: ControllerIdentity;
}

/**
 * Joins Live Share and builds a `SessionController` over it. Exported
 * separately from `createControllerFactory` so tests (or a caller that
 * wants to know whether Live Share specifically succeeded) can call it
 * directly; `createControllerFactory` is what the UI actually imports.
 */
export async function createLiveSessionController(
  options: CreateControllerOptions
): Promise<SessionController> {
  const initial = buildInitialSession(options.settings);
  const joined = await joinLiveShare(initial);
  const turnTimer = new TurnTimer(joined.timer);
  return new LiveSessionController(
    joined.session,
    turnTimer,
    joined.presence,
    joined.now,
    options.identity
  );
}

/**
 * Builds the `ControllerFactory` the UI imports (`src/contract.ts`).
 * `ControllerFactory` itself takes no arguments, so the settings/identity
 * this session needs are captured here as a closure at app start-up — the
 * caller (main.tsx) is expected to have already loaded the two persisted
 * settings (BUILD-SPEC hard rule 2) and the current Teams user's
 * display name/id before building this factory.
 *
 * Never rejects (BUILD-SPEC §5 "Fallback" / contract.ts doc): if
 * `joinContainer` throws for any reason (no Live Share host, tenant not
 * enabled, offline, ...), this falls back to the single-machine
 * `src/local/localTimer.ts` controller instead.
 */
export function createControllerFactory(options: CreateControllerOptions): ControllerFactory {
  return async () => {
    try {
      return await createLiveSessionController(options);
    } catch {
      return createLocalController(options);
    }
  };
}
